import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Package, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast, Toaster } from 'sonner';

const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const ActivatePurchase = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [tradingviewEmail, setTradingviewEmail] = useState('');
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (token) {
      fetchOrderInfo();
    } else {
      setLoading(false);
      setError('No activation token provided');
    }
  }, [token]);

  const fetchOrderInfo = async () => {
    try {
      const res = await fetch(`${API}/purchase/activate/${token}`);
      const data = await res.json();
      
      if (data.success) {
        setOrderInfo(data.order);
      } else {
        setError(data.message || 'Invalid activation link');
      }
    } catch (err) {
      setError('Failed to load order information');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    
    // Validate TradingView email if required
    if (orderInfo?.requires_tradingview_email && !tradingviewEmail.trim()) {
      toast.error('Please enter your TradingView email address');
      return;
    }
    
    if (orderInfo?.requires_tradingview_email && !tradingviewEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setActivating(true);
    
    try {
      const res = await fetch(`${API}/purchase/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          tradingview_email: tradingviewEmail || null
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setActivated(true);
        toast.success('Your purchase has been activated!');
      } else {
        toast.error(data.message || 'Activation failed');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your purchase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Activation Error</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500"
          >
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  if (activated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Purchase Activated!</h1>
          <p className="text-gray-400 mb-6">
            Your products have been linked to your profile. You can now access them from your dashboard.
          </p>
          <Button
            onClick={() => navigate('/portal')}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Activate Your Purchase</h1>
          <p className="text-gray-400">
            Order #{orderInfo?.order_id}
          </p>
        </div>

        {/* Order Items */}
        <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Your Purchase:</h3>
          <div className="space-y-2">
            {orderInfo?.cart_items?.map((item, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-white">{item.name}</span>
                <span className="text-green-400">${item.price?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* TradingView Email Input (only for indicator purchases) */}
        {orderInfo?.requires_tradingview_email && (
          <div className="mb-6">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-amber-400 font-medium mb-2">
                <Mail className="w-5 h-5" />
                TradingView Email Required
              </div>
              <p className="text-sm text-gray-300">
                Your purchase includes TradingView indicators. Please provide your TradingView account email to receive access.
              </p>
            </div>
            
            <label className="block text-sm text-gray-400 mb-2">TradingView Email Address</label>
            <input
              type="email"
              value={tradingviewEmail}
              onChange={(e) => setTradingviewEmail(e.target.value)}
              className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
              placeholder="your-tradingview@email.com"
              required
            />
            <p className="text-xs text-gray-500 mt-2">
              This email will be used to grant you access to the TradingView indicators.
            </p>
          </div>
        )}

        {/* Activate Button */}
        <form onSubmit={handleActivate}>
          <Button
            type="submit"
            disabled={activating}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-6 text-lg font-bold"
          >
            {activating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Activating...
              </>
            ) : (
              'Activate My Purchase'
            )}
          </Button>
        </form>

        {/* Info */}
        <p className="text-gray-500 text-sm text-center mt-6">
          Hi {orderInfo?.full_name?.split(' ')[0]}, click above to link these products to your profile at {orderInfo?.email}.
        </p>
      </div>
    </div>
  );
};

export default ActivatePurchase;
